import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from '@mui/material'
import SEOHead from '../components/SEO/SEOHead'
import AutonomyLadder from '../components/AutonomyLadder'
import { Page, Dobra, HeroLoop, Prose, type HeroLoopCopy } from '../brand'
import { SUITE_ROUTES } from '../brand/templates/routes'
import { SuiteCards } from '../components/sections/SuiteCards'
import { PlansStrip } from '../components/sections/Plans'
import { Committee } from '../components/sections/Committee'
import { ProofSection } from '../components/sections/Proof'
import { FONTE_EXEC } from '../components/sections/fonte'
import { ContactBlock } from '../components/sections/ContactBlock'
import { trackCTAClick } from '../utils/analytics'

/**
 * Home (D25, modelo do Ângelo + o que já era nosso): a Zentriz como empresa de IA apresentada pela Autonomy Suite.
 * Dobras: herói em tinta (HeroLoop: terminal → laço → tela) → a suíte [papel] → quatro planos, um laço [papel] → níveis L0–L5 [tinta]
 *         → a sala de decisão [papel] → o que aconteceu nesta execução [tinta, Rastro] → contato [tinta]. Sem imagem, sem foto (cap. 08).
 */
export default function Home() {
  const { t } = useTranslation()
  const loop = t('model.hero.loop', { returnObjects: true }) as HeroLoopCopy
  const homeSchema = {
    '@context': 'https://schema.org', '@type': 'Organization', name: 'Zentriz', url: 'https://zentriz.com.br', logo: 'https://zentriz.com.br/icons/logo-512.png',
    description: t('meta.description'), knowsAbout: ['Artificial intelligence', 'Autonomous software engineering', 'AIOps'],
    makesOffer: SUITE_ROUTES.map((r) => ({ '@type': 'Offer', itemOffered: { '@type': 'SoftwareApplication', name: `Zentriz ${t(`header.${r.slug}`)}`, url: `https://zentriz.com.br${r.to}`, applicationCategory: 'DeveloperApplication' } })),
  }
  return (
    <>
      <SEOHead title={t('home.hero.title')} description={t('meta.description')} keywords={['inteligência artificial', 'agentes de IA', 'engenharia de software autônoma', 'Autonomy Suite', 'Genesis', 'Connect', 'Auto Care', 'spec-to-system', 'incident autopilot', 'AIOps']} canonical="https://zentriz.com.br/" structuredData={homeSchema} />
      <Page state="construcao">
        <Dobra hero surface="dark" kicker={t('brand.pages.home.kicker')} title={t('brand.pages.home.title')} lede={t('model.hero.subtitle')} aside={<HeroLoop copy={loop} />}>
          <p className="z-cta-row">
            <Button component={Link} to="/contacts" variant="contained" onClick={() => trackCTAClick('home.cta.contact', 'hero', 'Home')}>{t('model.hero.ctaPrimary')}</Button>
            <Button component={Link} to="/plans" variant="outlined" onClick={() => trackCTAClick('home.cta.plans', 'hero', 'Home')}>{t('model.hero.ctaSecondary')}</Button>
          </p>
        </Dobra>

        <Dobra stack kicker={t('brand.pages.autonomySuite.kicker')} title={t('model.suite.title')} lede={t('brand.pages.autonomySuite.lede')}>
          <SuiteCards location="home" />
        </Dobra>

        <Dobra stack kicker={t('brand.plans.planWord')} title={t('model.plansStrip.title')} lede={t('model.plansStrip.lede')}>
          <Prose compacto><p>{t('model.plansStrip.body')}</p></Prose>
          <PlansStrip />
        </Dobra>

        <Dobra stack surface="dark" kicker={t('brand.pages.autonomySuite.kicker')} title={t('model.ladder.title')} lede={t('products.autonomyLevels.subtitle')}>
          <AutonomyLadder />
        </Dobra>

        <Dobra stack kicker={t('brand.pages.home.kicker')} title={t('model.committee.title')} lede={t('model.committee.lede')}>
          <Prose compacto><p>{t('model.committee.body')}</p></Prose>
          <Committee />
        </Dobra>

        <Dobra stack surface="dark" kicker={t('products.proof.badge')} title={t('model.proof.title')} lede={t('products.proof.subtitle')} rastro={FONTE_EXEC}>
          <ProofSection />
        </Dobra>

        <ContactBlock page="home" />
      </Page>
    </>
  )
}
